import { useCallback, useEffect, useMemo, useState } from 'react';
import { BadgeEuro, FileDown } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import { buildDatevCsv, downloadDatevCsv } from '@forq/finance-data';
import {
  DataTable,
  type DataTableColumn,
  CustomizableMetrics,
  MetricTile,
  StatusPill,
  WorkspacePage,
  useContextMenu,
  type ContextMenuItem,
} from '@forq/ui';
import { FinanceNav } from './FinanceNav';
import { buildPayablesOpList, buildReceivablesOpList, openOpList, renderOpListHtml, type OpListPartnerContacts } from './opListDocument';
import { isInvoiceOverdue, todayIso, type FinanceGateway, type Invoice } from '@forq/finance-data';
import { incomingStatusLabels, type IncomingInvoice, type IncomingInvoiceGateway } from '@forq/finance-data';
import type { CustomersGateway } from '@forq/customers-data';
import type { MasterdataGateway } from '@forq/masterdata-data';

interface OposPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly financeGateway: FinanceGateway;
  readonly incomingGateway: IncomingInvoiceGateway;
  readonly customersGateway: CustomersGateway;
  readonly masterdataGateway: MasterdataGateway;
}

/**
 * Lädt alle Seiten einer paginierten Stammdaten-Suche. Nutzt die vom Server ZURÜCKGEGEBENE Seitengröße
 * (die Suche klammert pageSize serverseitig, z. B. auf 100), damit die Seitenzahl stimmt; Sicherheits-Deckel 40 Seiten.
 */
async function loadAllPages<T>(
  fetchPage: (page: number, pageSize: number) => Promise<{ items: readonly T[]; total: number; pageSize: number }>,
): Promise<readonly T[]> {
  const first = await fetchPage(1, 100);
  const size = first.pageSize || 100;
  const pageCount = Math.min(Math.max(1, Math.ceil((first.total || first.items.length) / size)), 40);
  if (pageCount <= 1) return first.items;
  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_unused, index) => fetchPage(index + 2, size)),
  );
  return [first.items, ...rest.map((page) => page.items)].flat();
}

function money(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}
function dateLabel(value: string | null): string {
  return value ? new Intl.DateTimeFormat('de-DE').format(new Date(`${value}T00:00:00`)) : '–';
}
function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Der Vorgang konnte nicht verarbeitet werden.';
}
function incomingOverdue(invoice: IncomingInvoice, todayIsoDate: string): boolean {
  return invoice.status !== 'paid' && invoice.dueDate !== null && invoice.dueDate < todayIsoDate;
}

export function OposPage({ tenant, mode, permissions, financeGateway, incomingGateway, customersGateway, masterdataGateway }: OposPageProps) {
  const [invoices, setInvoices] = useState<readonly Invoice[]>([]);
  const [incoming, setIncoming] = useState<readonly IncomingInvoice[]>([]);
  const [customerContacts, setCustomerContacts] = useState<OpListPartnerContacts>(() => new Map());
  const [supplierContacts, setSupplierContacts] = useState<OpListPartnerContacts>(() => new Map());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = todayIso();
  const canPay = permissions.has('finance.payments.record');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [invoiceList, incomingList] = await Promise.all([
        financeGateway.listInvoices({ tenantId: tenant.id }),
        incomingGateway.listIncomingInvoices({ tenantId: tenant.id }),
      ]);
      setInvoices(invoiceList);
      setIncoming(incomingList);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [financeGateway, incomingGateway, tenant.id]);

  useEffect(() => { void load(); }, [load]);

  // Kunden-/Lieferanten-Stammdaten für Ort + Telefon in den OP-Listen-Gruppenköpfen (wie in der ARISTA-Vorlage).
  // Bewusst entkoppelt und fehlertolerant: schlägt das fehl, wird die OP-Liste einfach ohne Kontaktzeile gedruckt.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [customers, suppliers] = await Promise.all([
          loadAllPages((page, pageSize) => customersGateway.searchCustomers({ tenantId: tenant.id, status: 'all', page, pageSize })),
          loadAllPages((page, pageSize) => masterdataGateway.searchSuppliers({ tenantId: tenant.id, status: 'all', page, pageSize })),
        ]);
        if (cancelled) return;
        setCustomerContacts(new Map(customers.map((customer) => [customer.number, { city: customer.city, phone: customer.phone }])));
        setSupplierContacts(new Map(suppliers.map((supplier) => [supplier.number, { city: supplier.city, phone: supplier.phone }])));
      } catch {
        // Ohne Stammdaten-Kontakte drucken.
      }
    })();
    return () => { cancelled = true; };
  }, [customersGateway, masterdataGateway, tenant.id]);

  const receivables = useMemo(
    () => invoices.filter((i) => i.paymentStatus === 'open' || i.paymentStatus === 'partial'),
    [invoices],
  );
  const payables = useMemo(
    () => incoming.filter((i) => !['paid', 'cancelled', 'rejected'].includes(i.status)),
    [incoming],
  );

  const metrics = useMemo(() => ({
    receivablesSum: receivables.reduce((s, i) => s + (i.grossTotal - i.paidAmount), 0),
    payablesSum: payables.reduce((s, i) => s + i.grossAmount, 0),
    receivablesOverdue: receivables.filter((i) => isInvoiceOverdue(i, today)).length,
    payablesOverdue: payables.filter((i) => incomingOverdue(i, today)).length,
  }), [receivables, payables, today]);

  const collectReceivable = useCallback(async (invoice: Invoice) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await financeGateway.recordPayment({ tenantId: tenant.id, invoiceId: invoice.id, amount: invoice.grossTotal - invoice.paidAmount });
      setInvoices((current) => current.map((i) => i.id === updated.id ? updated : i));
    } catch (paymentError) {
      setError(errorMessage(paymentError));
    } finally {
      setBusy(false);
    }
  }, [financeGateway, tenant.id]);

  const payPayable = useCallback(async (invoice: IncomingInvoice) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await incomingGateway.transitionIncoming({ tenantId: tenant.id, id: invoice.id, toStatus: 'paid' });
      setIncoming((current) => current.map((i) => i.id === updated.id ? updated : i));
    } catch (paymentError) {
      setError(errorMessage(paymentError));
    } finally {
      setBusy(false);
    }
  }, [incomingGateway, tenant.id]);

  const exportDatev = useCallback(() => {
    downloadDatevCsv(buildDatevCsv(invoices, incoming), tenant.shortName);
  }, [invoices, incoming, tenant.shortName]);

  const companyName = tenant.name || tenant.shortName;
  const printReceivablesList = useCallback(() => {
    openOpList(renderOpListHtml('debitor', companyName, today, buildReceivablesOpList(invoices, today, customerContacts)));
  }, [invoices, today, companyName, customerContacts]);
  const printPayablesList = useCallback(() => {
    openOpList(renderOpListHtml('kreditor', companyName, today, buildPayablesOpList(incoming, today, supplierContacts)));
  }, [incoming, today, companyName, supplierContacts]);

  const { open: openOposMenu, menu: oposMenu } = useContextMenu();
  const receivableMenuItems = useCallback((i: Invoice): ContextMenuItem[] => (
    canPay ? [{ label: 'Zahlungseingang buchen', icon: <BadgeEuro size={15} />, disabled: busy, onSelect: () => void collectReceivable(i) }] : []
  ), [canPay, busy, collectReceivable]);
  const payableMenuItems = useCallback((i: IncomingInvoice): ContextMenuItem[] => (
    canPay && i.status === 'payment_released'
      ? [{ label: 'Zahlung buchen', icon: <BadgeEuro size={15} />, disabled: busy, onSelect: () => void payPayable(i) }]
      : []
  ), [canPay, busy, payPayable]);

  const receivableColumns: readonly DataTableColumn<Invoice>[] = [
    { key: 'number', header: 'Rechnung', width: 'minmax(0, 1fr)', render: (i) => <span className="forq-stack"><strong>{i.invoiceNumber ?? i.proposalNumber}</strong><small>{i.customerName}</small></span> },
    { key: 'open', header: 'Offen', width: '120px', align: 'end', render: (i) => <span className="forq-cell-num">{money(i.grossTotal - i.paidAmount)}</span> },
    { key: 'due', header: 'Fällig', width: '110px', render: (i) => <span className={isInvoiceOverdue(i, today) ? 'finance-overdue' : undefined}>{dateLabel(i.dueDate)}</span> },
    {
      key: 'action', header: '', width: '150px', align: 'end',
      render: (i) => canPay ? <button type="button" className="finance-actions__btn is-pay" disabled={busy} onClick={() => void collectReceivable(i)}><BadgeEuro size={13} /> Zahlungseingang</button> : null,
    },
  ];

  const payableColumns: readonly DataTableColumn<IncomingInvoice>[] = [
    { key: 'number', header: 'Beleg', width: 'minmax(0, 1fr)', render: (i) => <span className="forq-stack"><strong>{i.number}</strong><small>{i.supplierName}</small></span> },
    { key: 'gross', header: 'Brutto', width: '120px', align: 'end', render: (i) => <span className="forq-cell-num">{money(i.grossAmount)}</span> },
    { key: 'due', header: 'Fällig', width: '110px', render: (i) => <span className={incomingOverdue(i, today) ? 'finance-overdue' : undefined}>{dateLabel(i.dueDate)}</span> },
    {
      key: 'action', header: '', width: '170px', align: 'end',
      render: (i) => i.status === 'payment_released'
        ? (canPay ? <button type="button" className="finance-actions__btn is-pay" disabled={busy} onClick={() => void payPayable(i)}><BadgeEuro size={13} /> Zahlung buchen</button> : null)
        : <StatusPill label={incomingStatusLabels[i.status]} tone="warning" />,
    },
  ];

  return (
    <WorkspacePage
      customizeKey="finance-opos"
      eyebrow={`FORQ Finance · ${mode === 'demo' ? 'Demo' : 'Supabase'}`}
      title="Offene Posten & Zahlungslauf"
      description="Debitoren und Kreditoren auf einen Blick – Zahlungseingänge erfassen, freigegebene Verbindlichkeiten bezahlen."
      actions={(
        <>
          <button type="button" className="finance-actions__btn" onClick={printReceivablesList}><FileDown size={15} /> Debitoren-OP-Liste</button>
          <button type="button" className="finance-actions__btn" onClick={printPayablesList}><FileDown size={15} /> Kreditoren-OP-Liste</button>
          <button type="button" className="finance-actions__btn" onClick={exportDatev}><FileDown size={15} /> DATEV-Export</button>
        </>
      )}
      metrics={(
        <CustomizableMetrics
          pageKey="finance-opos"
          tiles={[
            { key: 'receivables', node: <MetricTile label="Forderungen offen" value={money(metrics.receivablesSum)} hint={`${receivables.length} Debitoren`} tone="info" /> },
            { key: 'payables', node: <MetricTile label="Verbindlichkeiten offen" value={money(metrics.payablesSum)} hint={`${payables.length} Kreditoren`} tone="warning" /> },
            { key: 'rec-overdue', node: <MetricTile label="Überfällig (Deb.)" value={metrics.receivablesOverdue} hint="Forderungen" tone={metrics.receivablesOverdue > 0 ? 'danger' : 'default'} /> },
            { key: 'pay-overdue', node: <MetricTile label="Überfällig (Kred.)" value={metrics.payablesOverdue} hint="Verbindlichkeiten" tone={metrics.payablesOverdue > 0 ? 'danger' : 'default'} /> },
          ]}
        />
      )}
      toolbar={<div className="finance-toolbar"><FinanceNav /></div>}
    >
      {error && <div className="finance-page__error">{error}</div>}

      <section className="finance-opos-section">
        <h3 className="finance-section__title">Debitoren · Forderungen</h3>
        <DataTable
          columns={receivableColumns}
          rows={receivables}
          rowKey={(i) => i.id}
          rowTone={(i) => isInvoiceOverdue(i, today) ? 'danger' : undefined}
          onRowContext={(event, i) => openOposMenu(event, receivableMenuItems(i))}
          loading={loading}
          loadingLabel="Forderungen werden geladen …"
          emptyLabel="Keine offenen Forderungen."
          ariaLabel="Offene Forderungen"
        />
      </section>

      <section className="finance-opos-section">
        <h3 className="finance-section__title">Kreditoren · Verbindlichkeiten <small>(Zahlungslauf: freigegebene zahlen)</small></h3>
        <DataTable
          columns={payableColumns}
          rows={payables}
          rowKey={(i) => i.id}
          rowTone={(i) => incomingOverdue(i, today) ? 'danger' : undefined}
          onRowContext={(event, i) => openOposMenu(event, payableMenuItems(i))}
          loading={loading}
          loadingLabel="Verbindlichkeiten werden geladen …"
          emptyLabel="Keine offenen Verbindlichkeiten."
          ariaLabel="Offene Verbindlichkeiten"
        />
      </section>
      {oposMenu}
    </WorkspacePage>
  );
}
